import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Video, Calendar, Plus, PhoneCall, PhoneOff, Send, FileText, Users,
  MessageSquare, MonitorUp, Mic, MicOff, Camera, CameraOff, Activity,
  Heart, PhoneIncoming, FileCheck2,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FormField } from "@/components/common/FormSection";
import { MultiSelect } from "@/components/common/MultiSelect";
import AttachmentsPanel from "@/components/common/AttachmentsPanel";
import SlotPreview from "@/pages/profile/SlotPreview";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_COLORS = {
  scheduled: { bg: "#DBEAFE", text: "#1E40AF", label: "Scheduled" },
  awaiting_participants: { bg: "#FEF3C7", text: "#92400E", label: "Awaiting" },
  in_progress: { bg: "#D1FAE5", text: "#065F46", label: "In Progress" },
  completed: { bg: "#F1F5F9", text: "#475569", label: "Completed" },
  cancelled: { bg: "#FEE2E2", text: "#991B1B", label: "Cancelled" },
  no_show: { bg: "#FED7AA", text: "#9A3412", label: "No Show" },
};
const TYPES = {
  initial_assessment: "Initial Assessment", follow_up: "Follow-up",
  rpm_escalation: "RPM Escalation", hotline_escalation: "Hotline Escalation",
  physician_review: "Physician Review", nursing: "Nursing Consult",
  medication_review: "Medication Review", multidisciplinary: "Multidisciplinary",
};
function fmtAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60000) return "just now";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ---------- DASHBOARD ----------
function Kpi({ label, value, accent, icon: Icon }) {
  return (
    <div className="hah-card p-3 flex items-start gap-3">
      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}14`, color: accent }}>
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <div className="text-2xl font-bold text-[#1F2937]">{value ?? "—"}</div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</div>
      </div>
    </div>
  );
}

export function TeleconsultDashboard() {
  const navigate = useNavigate();
  const [k, setK] = useState({});
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", consultation_type: "follow_up", provider_ids: [], scheduled_at: "", duration_minutes: 30, priority: "routine", notes: "", include_patient: true, instant: false });
  const [schedDate, setSchedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [patients, setPatients] = useState([]);
  const [providers, setProviders] = useState([]);

  // ---------- Filters (G6) ----------
  const [filters, setFilters] = useState({
    q: "",
    status: "all",
    consultation_type: "all",
    source_module: "all",
    outcome: "all",
    date_from: "",
    date_to: "",
  });
  const [outcomes, setOutcomesList] = useState([]);

  // Load outcomes lookup once
  useEffect(() => {
    api.get("/teleconsult/outcomes").then((r) => setOutcomesList(r.data.items || r.data || [])).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const p = { page_size: 100 };
    Object.entries(filters).forEach(([key, val]) => {
      if (val && val !== "all" && val !== "") p[key] = val;
    });
    return p;
  }, [filters]);

  // KPIs and list are loaded independently so a slow/failing KPI fetch never blocks the grid.
  const loadKpis = useCallback(async () => {
    try {
      const d = await api.get("/teleconsult/dashboard");
      setK(d.data.kpis || {});
    } catch (e) {
      // Soft fail — KPIs are non-critical; keep grid functional.
      console.warn("KPI fetch failed", e?.message);
    }
  }, []);

  const loadList = useCallback(async () => {
    try {
      const l = await api.get("/teleconsult/consultations", { params: buildParams() });
      setList(l.data.items || []);
      setTotal(l.data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [buildParams]);

  // Initial mount: load both
  useEffect(() => {
    loadKpis();
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload list immediately on structured filter changes
  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.status, filters.consultation_type, filters.source_module,
    filters.outcome, filters.date_from, filters.date_to,
  ]);

  // Debounced reload for free-text search
  useEffect(() => {
    const t = setTimeout(() => { loadList(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q]);

  // Public alias used by post-action callbacks (e.g. after creating a teleconsult)
  const load = useCallback(() => { loadKpis(); loadList(); }, [loadKpis, loadList]);

  function setFilter(k, v) { setFilters((f) => ({ ...f, [k]: v })); }
  function clearFilters() {
    setFilters({ q: "", status: "all", consultation_type: "all", source_module: "all", outcome: "all", date_from: "", date_to: "" });
  }
  const activeFilterCount = Object.entries(filters).filter(([_k, v]) => v && v !== "all" && v !== "").length;

  useEffect(() => {
    if (!newOpen) return;
    (async () => {
      try {
        const [p, u] = await Promise.all([
          api.get("/patients", { params: { page_size: 100 } }),
          api.get("/users", { params: { page_size: 100 } }),
        ]);
        setPatients(p.data.items || []);
        setProviders((u.data.items || []).filter((x) => ["physician", "hah_physician", "hah_nurse", "nurse"].includes(x.role_code)));
      } catch {}
    })();
  }, [newOpen]);

  async function create() {
    if (form.include_patient && !form.patient_id) { toast.error("Pick a patient or uncheck 'Include patient'"); return; }
    if (!form.provider_ids?.length && !form.include_patient) { toast.error("Pick at least one provider"); return; }
    if (!form.instant && form.provider_ids?.length && !form.scheduled_at) { toast.error("Pick an available slot for the provider"); return; }
    try {
      const [primary_provider_id, ...participants] = form.provider_ids;
      const r = await api.post("/teleconsult/consultations", {
        ...form,
        patient_id: form.include_patient ? form.patient_id : null,
        primary_provider_id: primary_provider_id || null,
        participants,
        scheduled_at: form.scheduled_at || null,
        provider_ids: undefined,
      });
      toast.success(form.instant ? "Instant call started — links sent" : "Consultation scheduled — links sent");
      setNewOpen(false); load();
      navigate(`/teleconsult/room/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="teleconsult-dashboard">
      <PageHeader title="Teleconsultation" subtitle="Virtual care sessions across the H@H ecosystem." actions={
        <Button onClick={() => setNewOpen(true)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="tc-new">
          <Plus className="w-4 h-4 mr-1.5" /> Schedule Consultation
        </Button>
      } />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        <Kpi label="Today" value={k.today} accent="#0B7E8C" icon={Calendar} />
        <Kpi label="Active" value={k.active} accent="#10B981" icon={Video} />
        <Kpi label="Upcoming" value={k.upcoming} accent="#3B82F6" icon={Calendar} />
        <Kpi label="Completed" value={k.completed} accent="#475569" icon={FileCheck2} />
        <Kpi label="Cancelled" value={k.cancelled} accent="#EF4444" icon={PhoneOff} />
        <Kpi label="No Show" value={k.no_show} accent="#F97316" icon={PhoneOff} />
        <Kpi label="RPM Escalated" value={k.rpm_escalated} accent="#8B5CF6" icon={Activity} />
        <Kpi label="Hotline Escalated" value={k.hotline_escalated} accent="#F59E0B" icon={PhoneIncoming} />
        <Kpi label="Avg Duration" value={`${k.avg_duration_min || 0}m`} accent="#0B7E8C" icon={Video} />
      </div>

      {/* ---------- Filter Bar (G6) ---------- */}
      <div className="hah-card p-3 mb-3" data-testid="tc-filter-bar">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-2">
          <div className="lg:col-span-2">
            <label className="block text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Search</label>
            <Input
              value={filters.q}
              onChange={(e) => setFilter("q", e.target.value)}
              placeholder="Patient, MRN, room ID, session ID, user…"
              className="h-9"
              data-testid="tc-filter-q"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Status</label>
            <Select value={filters.status} onValueChange={(v) => setFilter("status", v)}>
              <SelectTrigger className="h-9" data-testid="tc-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {Object.entries(STATUS_COLORS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Type</label>
            <Select value={filters.consultation_type} onValueChange={(v) => setFilter("consultation_type", v)}>
              <SelectTrigger className="h-9" data-testid="tc-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Source</label>
            <Select value={filters.source_module} onValueChange={(v) => setFilter("source_module", v)}>
              <SelectTrigger className="h-9" data-testid="tc-filter-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="rpm">RPM</SelectItem>
                <SelectItem value="hotline">Hotline</SelectItem>
                <SelectItem value="onboarding">Onboarding</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Outcome</label>
            <Select value={filters.outcome} onValueChange={(v) => setFilter("outcome", v)}>
              <SelectTrigger className="h-9" data-testid="tc-filter-outcome"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {outcomes.map((o) => (
                  <SelectItem key={o.code || o.value || o} value={o.code || o.value || o}>{o.label || o.name || o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-2">
            <label className="block text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Date Range</label>
            <div className="flex items-center gap-1">
              <Input type="date" value={filters.date_from} onChange={(e) => setFilter("date_from", e.target.value)} className="h-9 text-xs flex-1 min-w-0" data-testid="tc-filter-from" />
              <Input type="date" value={filters.date_to} onChange={(e) => setFilter("date_to", e.target.value)} className="h-9 text-xs flex-1 min-w-0" data-testid="tc-filter-to" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="text-xs text-[#6B7280]" data-testid="tc-result-count">
            Showing <span className="font-semibold text-[#1F2937]">{list.length}</span> of <span className="font-semibold text-[#1F2937]">{total}</span>
            {activeFilterCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-[#0B7E8C]">
                · {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""} active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <Button size="sm" variant="ghost" onClick={clearFilters} className="h-7 text-xs" data-testid="tc-filter-clear">
                Clear filters
              </Button>
            )}
            <Button
              size="sm" variant="outline" className="h-7 text-xs border-[#E5E7EB]"
              onClick={() => navigate("/reports?module=teleconsult")}
              data-testid="tc-filter-reports"
            >
              <FileText className="w-3.5 h-3.5 mr-1" /> Reports & Exports
            </Button>
          </div>
        </div>
      </div>

      <div className="hah-card overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-[#F5F7FA] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr><th className="text-left p-2.5">Type</th><th className="text-left p-2.5">Patient</th><th className="text-left p-2.5">Provider</th><th className="text-left p-2.5">Source</th><th className="text-left p-2.5">Scheduled</th><th className="text-left p-2.5">Status</th><th className="text-left p-2.5">Outcome</th><th className="text-right p-2.5">Action</th></tr>
          </thead>
          <tbody>
            {list.map((c) => {
              const sc = STATUS_COLORS[c.status] || {};
              return (
                <tr key={c.id} className="border-t border-[#E5E7EB] hover:bg-[#FAFBFC]" data-testid={`tc-row-${c.id}`}>
                  <td className="p-2.5 font-medium">{TYPES[c.consultation_type] || c.consultation_type}</td>
                  <td className="p-2.5">{c.patient_name} <span className="text-xs text-[#94A3B8] font-mono">{c.patient_mrn}</span></td>
                  <td className="p-2.5 text-[#6B7280]">{c.provider_name || "—"}</td>
                  <td className="p-2.5 text-[10px] uppercase tracking-wider text-[#6B7280]">{c.source_module || "manual"}</td>
                  <td className="p-2.5 text-[#6B7280]">{c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : "—"}</td>
                  <td className="p-2.5"><span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider" style={{ background: sc.bg, color: sc.text }}>{sc.label}</span></td>
                  <td className="p-2.5 text-xs text-[#475569]">{c.outcome ? (outcomes.find((o) => (o.code || o.value) === c.outcome)?.label || c.outcome).replace(/_/g, " ") : "—"}</td>
                  <td className="p-2.5 text-right">
                    <Button size="sm" onClick={() => navigate(`/teleconsult/room/${c.id}`)} className="h-7 text-xs bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid={`tc-open-${c.id}`}>
                      <Video className="w-3 h-3 mr-1" /> Open Room
                    </Button>
                  </td>
                </tr>
              );
            })}
            {list.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-sm text-[#6B7280]">{activeFilterCount > 0 ? "No consultations match your filters." : "No consultations yet — schedule one to get started."}</td></tr>}
          </tbody>
        </table></div>
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Schedule Consultation</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-3 text-xs">
              <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="tc-form-include-patient-wrap">
                <input
                  type="checkbox"
                  checked={form.include_patient}
                  onChange={(e) => setForm({ ...form, include_patient: e.target.checked })}
                  data-testid="tc-form-include-patient"
                />
                <span className="font-semibold text-[#1F2937]">Include patient in this call</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="tc-form-instant-wrap">
                <input
                  type="checkbox"
                  checked={form.instant}
                  onChange={(e) => setForm({ ...form, instant: e.target.checked, scheduled_at: e.target.checked ? "" : form.scheduled_at })}
                  data-testid="tc-form-instant"
                />
                <span className="font-semibold text-[#1F2937]">Start now (instant call)</span>
              </label>
            </div>
            {form.include_patient && (
              <FormField label="Patient" required>
                <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                  <SelectTrigger data-testid="tc-form-patient"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} · {p.mrn}</SelectItem>)}</SelectContent>
                </Select>
              </FormField>
            )}
            <FormField label="Type">
              <Select value={form.consultation_type} onValueChange={(v) => setForm({ ...form, consultation_type: v })}>
                <SelectTrigger data-testid="tc-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
            <FormField label="Providers">
              <MultiSelect
                testid="tc-form-providers"
                placeholder="Select one or more…"
                primaryLabel="Primary"
                emptyText="No providers found"
                values={form.provider_ids}
                onChange={(ids) => setForm({ ...form, provider_ids: ids })}
                options={providers.map((u) => ({
                  value: u.id,
                  label: `${u.first_name} ${u.last_name}`,
                  sublabel: u.role_code,
                }))}
              />
            </FormField>
            {!form.instant && (
              <FormField label="Scheduled at">
                {form.provider_ids[0] ? (
                  <SlotPreview
                    userIds={form.provider_ids}
                    title="Pick an available 30-min slot"
                    date={schedDate}
                    onDateChange={(d) => { setSchedDate(d); setForm((f) => ({ ...f, scheduled_at: "" })); }}
                    selected={form.scheduled_at && form.scheduled_at.startsWith(schedDate) ? form.scheduled_at.split("T")[1]?.slice(0, 5) : null}
                    onSelect={(slot, d) => setForm((f) => ({ ...f, scheduled_at: `${d}T${slot.start}`, duration_minutes: 30 }))}
                  />
                ) : (
                  <div className="text-sm text-[#6B7280] border border-dashed border-[#E5E7EB] rounded-lg p-3" data-testid="tc-form-no-provider-hint">
                    Select a provider above to see their available slots.
                  </div>
                )}
              </FormField>
            )}
            {!form.instant && (
              <FormField label="Duration (min)">
                <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 30 })} />
              </FormField>
            )}
            <FormField label="Priority">
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="routine">Routine</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Notes" full>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={create} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="tc-form-save">{form.instant ? "Start Call" : "Schedule"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- ROOM ----------
export function TeleconsultRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [msg, setMsg] = useState("");
  const [endOpen, setEndOpen] = useState(false);
  const [iframeFailed, setIframeFailed] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [outcomes, setOutcomes] = useState([]);
  const [enc, setEnc] = useState({ assessment: "", diagnosis: "", recommendations: "", outcome: "", follow_up_required: false, follow_up_notes: "" });

  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([api.get(`/teleconsult/consultations/${id}`), api.get(`/teleconsult/consultations/${id}/context`)]);
      setData(r.data); setCtx(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Auto-poll messages every 4s (very lightweight)
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/teleconsult/consultations/${id}`);
        setData(r.data);
      } catch {}
    }, 4000);
    return () => clearInterval(t);
  }, [id]);

  useEffect(() => {
    api.get("/teleconsult/outcomes").then((r) => setOutcomes(r.data.items || [])).catch(() => {});
  }, []);

  async function start() {
    try { await api.post(`/teleconsult/consultations/${id}/start`); await api.post(`/teleconsult/consultations/${id}/join`); toast.success("Consultation started"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function send() {
    if (!msg.trim()) return;
    try { await api.post(`/teleconsult/consultations/${id}/messages`, { text: msg }); setMsg(""); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function end() {
    try {
      const r = await api.post(`/teleconsult/consultations/${id}/end`, enc);
      toast.success(`Encounter ${r.data.encounter.id.slice(0, 8)}… created`);
      setEndOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (!data) return <div className="p-6 text-sm text-[#6B7280]">Loading…</div>;
  const c = data.consultation;
  const status = c.status;
  const inProg = status === "in_progress";

  return (
    <div data-testid="teleconsult-room">
      <PageHeader title={`Consultation Room`}
        subtitle={`${ctx?.patient ? `${ctx.patient.first_name} ${ctx.patient.last_name} · ${ctx.patient.mrn}` : ""} · Status: ${status}`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/teleconsult")} className="border-[#E5E7EB]">Back</Button>
            {status !== "completed" && status !== "cancelled" && (
              <Button variant="outline" onClick={() => setInviteOpen(true)} className="border-[#0B7E8C] text-[#0B7E8C]" data-testid="tc-room-invite">
                <Plus className="w-4 h-4 mr-1.5" /> Invite Participant
              </Button>
            )}
            {!inProg && status !== "completed" && (
              <Button onClick={start} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="tc-room-start">
                <PhoneCall className="w-4 h-4 mr-1.5" /> Start
              </Button>
            )}
            {inProg && (
              <Button onClick={() => setEndOpen(true)} className="bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="tc-room-end">
                <PhoneOff className="w-4 h-4 mr-1.5" /> End & Create Encounter
              </Button>
            )}
          </div>
        } />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* MAIN ROOM */}
        <div className="lg:col-span-2 space-y-3">
          {/* Live Watheeq video — embedded iframe with pop-out fallback */}
          {(() => {
            if (!c.video_room_id) {
              return (
                <div className="hah-card p-6 bg-[#0F172A] text-center text-white">
                  <Video className="w-10 h-10 mx-auto mb-2 text-[#0B7E8C]" />
                  <div className="text-sm">No video room attached to this consultation.</div>
                </div>
              );
            }
            // Strict match — never show someone else's credentials
            const invitees = c.video_invitees || [];
            const myEmail = (user?.email || "").toLowerCase().trim();
            const myInvite = invitees.find((iv) => (iv.email || "").toLowerCase().trim() === myEmail);

            if (!myInvite) {
              // Not on the invitee list — offer self-join (calls /invite with own user_id)
              return (
                <div className="hah-card p-6 bg-[#0F172A] text-white" data-testid="tc-not-invited-panel">
                  <Video className="w-10 h-10 mx-auto mb-3 text-[#0B7E8C]" />
                  <div className="text-center text-sm mb-1 font-semibold">
                    You aren&apos;t an invitee on this call
                  </div>
                  <div className="text-center text-xs text-[#94A3B8] mb-4">
                    Signed in as <span className="font-mono">{user?.email || "—"}</span>.
                    Add yourself to receive a personalized Watheeq link with your own OTP.
                  </div>
                  <div className="flex justify-center">
                    <Button
                      onClick={async () => {
                        try {
                          await api.post(`/teleconsult/consultations/${id}/invite`, { user_id: user.id });
                          toast.success("Added you to the call — refreshing room");
                          load();
                        } catch (e) { toast.error(formatApiError(e)); }
                      }}
                      className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
                      data-testid="tc-self-join"
                    >
                      <Plus className="w-4 h-4 mr-1.5" /> Add me to this call
                    </Button>
                  </div>
                </div>
              );
            }

            const baseLink = myInvite.link;
            const myName = (`${user?.first_name || ""} ${user?.last_name || ""}`.trim()) || myInvite.display_name || user?.email || "";
            // Try every common "skip lobby / direct join" convention so that whichever Watheeq honours, we hit it.
            // The hard cross-origin DOM access block means we can only signal intent via URL params.
            const skipParams = [
              `username=${encodeURIComponent(myName)}`,
              `name=${encodeURIComponent(myName)}`,
              `displayName=${encodeURIComponent(myName)}`,
              `autojoin=1`,
              `auto_join=true`,
              `autoJoin=true`,
              `direct=1`,
              `skipLobby=1`,
              `skip_lobby=true`,
              `joinNow=1`,
              `start=1`,
            ].join("&");
            const myLink = baseLink
              ? `${baseLink}${baseLink.includes("?") ? "&" : "?"}${skipParams}`
              : null;
            const hostInvite = myInvite;
            return (
              <div className="hah-card overflow-hidden bg-[#0F172A]" data-testid="tc-video-card">
                <div className="flex items-center justify-between px-3 py-2 bg-[#0B1220] text-white">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                    <span className="uppercase tracking-wider font-bold">Live · Watheeq</span>
                    <span className="text-[#94A3B8] font-mono">room {c.video_room_id}</span>
                    {hostInvite && (
                      <span className="text-[#94A3B8]">
                        · joined as <span className="text-white">{hostInvite.display_name}</span>
                        {hostInvite.otp ? <> · OTP <span className="font-mono font-bold text-[#FCD34D]">{hostInvite.otp}</span></> : null}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {myLink && (
                      <a
                        href={myLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[#0B7E8C] bg-white hover:bg-[#F5F7FA] rounded px-2 py-0.5 font-semibold whitespace-nowrap"
                        data-testid="tc-video-popout"
                      >
                        Pop out ↗
                      </a>
                    )}
                  </div>
                </div>
                {myLink ? (
                  <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                    {!iframeFailed ? (
                      <iframe
                        key={myLink}
                        src={myLink}
                        title="Watheeq video room"
                        className="absolute inset-0 w-full h-full border-0"
                        allow="camera; microphone; display-capture; autoplay; clipboard-read; clipboard-write; fullscreen"
                        allowFullScreen
                        onError={() => setIframeFailed(true)}
                        data-testid="tc-video-iframe"
                      />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0F172A] text-white p-6">
                        <Video className="w-10 h-10 mb-3 text-[#0B7E8C]" />
                        <div className="text-sm mb-3 text-center max-w-md">
                          Your browser blocked the embedded video frame. Use the pop-out below to open Watheeq directly.
                        </div>
                        <a
                          href={myLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-[#0B7E8C] hover:bg-[#128C97] text-white px-3 py-2 rounded text-sm font-semibold"
                        >
                          Open Watheeq Room ↗
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-8 text-center text-white">
                    <div className="text-sm mb-2">
                      No personalized link is available for your account ({user?.email || "—"}) on this call.
                    </div>
                    <div className="text-xs text-[#94A3B8]">
                      Either the call was scheduled before you were added, or your email doesn&apos;t match any invitee.
                      Use one of the invitee join links below.
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Watheeq invitee directory */}
          {c.video_room_id && (
            <div className="hah-card p-3" data-testid="tc-watheeq-card">
              <div className="flex items-center justify-between mb-2.5">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#0B7E8C]">
                    Invitee Join Links
                  </div>
                  <div className="text-xs text-[#6B7280]">
                    Personalized URLs + OTPs · share with anyone who didn&apos;t get the SMS.
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                {(c.video_invitees || []).map((inv, idx) => {
                  const statusColors = {
                    invited:  { bg: "#F1F5F9", text: "#475569", label: "Invited" },
                    ringing:  { bg: "#FEF3C7", text: "#92400E", label: "Ringing" },
                    joined:   { bg: "#D1FAE5", text: "#065F46", label: "Joined" },
                    declined: { bg: "#FEE2E2", text: "#991B1B", label: "Declined" },
                    missed:   { bg: "#FEE2E2", text: "#991B1B", label: "Missed" },
                    left:     { bg: "#E0E7FF", text: "#3730A3", label: "Left" },
                  };
                  const sc = statusColors[inv.invite_status] || statusColors.invited;
                  return (
                  <div
                    key={idx}
                    className="flex items-center justify-between gap-2 p-2 rounded bg-[#F5F7FA] hover:bg-[#EEF2F7]"
                    data-testid={`tc-watheeq-invitee-${idx}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#1F2937] truncate">
                        {inv.display_name}{" "}
                        <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                          · {inv.watheeq_role || inv.role}
                        </span>
                      </div>
                      <div className="text-[11px] text-[#6B7280] font-mono truncate">{inv.email}</div>
                    </div>
                    <span
                      className="px-1.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider"
                      style={{ background: sc.bg, color: sc.text }}
                      data-testid={`tc-invitee-status-${idx}`}
                    >
                      {sc.label}
                    </span>
                    {inv.otp && (
                      <span
                        className="px-1.5 py-0.5 rounded font-mono text-xs bg-[#FEF3C7] text-[#92400E] font-bold"
                        title="One-time PIN"
                      >
                        OTP {inv.otp}
                      </span>
                    )}
                    {inv.link && (
                      <>
                        <a
                          href={inv.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#0B7E8C] hover:underline font-semibold whitespace-nowrap"
                          data-testid={`tc-watheeq-open-${idx}`}
                        >
                          Open ↗
                        </a>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            await navigator.clipboard.writeText(inv.link);
                            toast.success(`Link for ${inv.display_name} copied`);
                          }}
                          className="border-[#E5E7EB] h-6 text-[10px] px-2"
                          data-testid={`tc-watheeq-copy-${idx}`}
                        >
                          Copy
                        </Button>
                      </>
                    )}
                  </div>
                  );
                })}
              </div>
              <p className="mt-2 text-[10px] text-[#6B7280] italic">
                Personalized join links + OTPs have been SMS&apos;d to each invitee with a phone number on file.
              </p>
            </div>
          )}

          {/* Tabs: Chat / Files / Notes */}
          <Tabs defaultValue="chat">
            <TabsList className="flex bg-transparent h-auto p-0 gap-1 mb-2">
              <TabsTrigger value="chat" className="hah-tab" data-testid="tc-tab-chat"><MessageSquare className="w-3.5 h-3.5 mr-1" />Chat</TabsTrigger>
              <TabsTrigger value="files" className="hah-tab" data-testid="tc-tab-files"><FileText className="w-3.5 h-3.5 mr-1" />Files</TabsTrigger>
              <TabsTrigger value="participants" className="hah-tab"><Users className="w-3.5 h-3.5 mr-1" />Participants</TabsTrigger>
            </TabsList>
            <TabsContent value="chat" className="mt-0">
              <div className="hah-card p-3 h-72 overflow-auto flex flex-col gap-2" data-testid="tc-chat-list">
                {data.messages.length === 0 && <div className="text-xs text-[#94A3B8] italic m-auto">No messages yet.</div>}
                {data.messages.map((m) => (
                  <div key={m.id} className="text-sm">
                    <span className="font-semibold text-[#0B7E8C]">{m.user_name}</span>
                    <span className="text-[10px] text-[#94A3B8] ml-2">{fmtAgo(m.at)}</span>
                    <div className="text-[#1F2937]">{m.text}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type a message…" onKeyDown={(e) => e.key === "Enter" && send()} data-testid="tc-chat-input" />
                <Button onClick={send} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="tc-chat-send"><Send className="w-4 h-4" /></Button>
              </div>
            </TabsContent>
            <TabsContent value="files" className="mt-0">
              <div className="hah-card p-3 min-h-[200px]" data-testid="tc-files-tab">
                <AttachmentsPanel
                  entityLabel="this consultation"
                  listUrl={`/teleconsult/consultations/${id}/attachments`}
                  uploadUrl={`/teleconsult/consultations/${id}/attachments`}
                  canUpload
                  deleteUrlFor={(a) => (user?.role_code === "admin" && a.uploaded_by_kind !== "patient"
                    ? `/teleconsult/consultations/${id}/attachments/${a.id}`
                    : null)}
                  testIdPrefix="tc-attachments"
                />
                {data.files.some((f) => f.url) && (
                  <div className="mt-4 pt-3 border-t border-[#E5E7EB]">
                    <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2">
                      Legacy shared links
                    </div>
                    <div className="space-y-2">
                      {data.files.filter((f) => f.url).map((f) => (
                        <a key={f.id} href={f.url} target="_blank" rel="noreferrer"
                          className="block p-2 rounded bg-[#F5F7FA] hover:bg-[#EAF8FA] text-sm">
                          <FileText className="w-3.5 h-3.5 inline mr-1.5" /> {f.filename}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
            <TabsContent value="participants" className="mt-0">
              <div className="hah-card p-3 space-y-1.5">
                {data.participants.map((p) => (
                  <div key={p.user_id} className="flex items-center justify-between text-sm">
                    <span>{p.name} <span className="text-xs text-[#94A3B8]">({p.role_code})</span></span>
                    <span className="text-xs text-[#6B7280]">{p.status} · {fmtAgo(p.joined_at)}</span>
                  </div>
                ))}
                {data.participants.length === 0 && <div className="text-xs text-[#94A3B8] italic">No participants joined yet.</div>}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* CLINICAL SIDEBAR */}
        <div className="space-y-3">
          {ctx?.patient && (
            <div className="hah-card p-4">
              <div className="hah-section-title">Patient Summary</div>
              <h3 className="text-base font-semibold text-[#1F2937] mt-1">{ctx.patient.first_name} {ctx.patient.last_name}</h3>
              <div className="text-xs text-[#6B7280]">{ctx.patient.mrn} · {ctx.patient.age}y · {ctx.patient.gender || "—"}</div>
              {ctx.patient.primary_diagnosis && <div className="text-xs text-[#6B7280] mt-1">{ctx.patient.primary_diagnosis}</div>}
            </div>
          )}
          {ctx?.rpm?.enrollment && (
            <div className="hah-card p-4">
              <div className="hah-section-title flex items-center gap-1"><Activity className="w-3 h-3" /> RPM</div>
              <div className="text-xs mt-1.5 space-y-0.5 text-[#6B7280]">
                <div>Diagnosis: <strong className="text-[#1F2937]">{ctx.rpm.enrollment.primary_diagnosis}</strong></div>
                <div>Active alerts: <strong className="text-[#EF4444]">{ctx.rpm.alerts.length}</strong></div>
                <div>Last readings: {ctx.rpm.latest_readings.slice(0, 3).map((r, i) => <span key={i} className="mr-2">{r.vital_type.replace("_", " ")} {r.value}{r.unit}</span>)}</div>
              </div>
            </div>
          )}
          {ctx?.hotline?.length > 0 && (
            <div className="hah-card p-4">
              <div className="hah-section-title flex items-center gap-1"><PhoneIncoming className="w-3 h-3" /> Recent Hotline</div>
              <div className="text-xs mt-1.5 space-y-1">
                {ctx.hotline.slice(0, 2).map((h) => (
                  <div key={h.id} className="text-[#6B7280]">
                    <strong className="text-[#1F2937]">{h.record_id}</strong> · {h.status} · {(h.chief_complaint || {}).description || ""}
                  </div>
                ))}
              </div>
            </div>
          )}
          {ctx?.encounters?.length > 0 && (
            <div className="hah-card p-4">
              <div className="hah-section-title">Recent Encounters</div>
              {ctx.encounters.map((e) => (
                <div key={e.id} className="text-xs text-[#6B7280] mt-1.5 border-t border-[#E5E7EB] pt-1.5">
                  <div className="font-semibold text-[#1F2937]">{e.consultation_type}</div>
                  <div>{fmtAgo(e.ended_at)} · {e.duration_minutes}min</div>
                  {e.assessment && <div className="line-clamp-2">{e.assessment}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* End consultation / Encounter dialog */}
      <Dialog open={endOpen} onOpenChange={setEndOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>End Consultation — Create Encounter</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <FormField label="Assessment" full>
              <Textarea rows={2} value={enc.assessment} onChange={(e) => setEnc({ ...enc, assessment: e.target.value })} data-testid="tc-end-assessment" />
            </FormField>
            <FormField label="Diagnosis"><Input value={enc.diagnosis} onChange={(e) => setEnc({ ...enc, diagnosis: e.target.value })} /></FormField>
            <FormField label="Recommendations" full>
              <Textarea rows={2} value={enc.recommendations} onChange={(e) => setEnc({ ...enc, recommendations: e.target.value })} data-testid="tc-end-recs" />
            </FormField>
            <FormField label="Outcome">
              <Select value={enc.outcome} onValueChange={(v) => setEnc({ ...enc, outcome: v })}>
                <SelectTrigger data-testid="tc-end-outcome"><SelectValue placeholder="Select an outcome…" /></SelectTrigger>
                <SelectContent>
                  {outcomes.map((o) => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Follow-up notes" full><Textarea rows={2} value={enc.follow_up_notes} onChange={(e) => setEnc({ ...enc, follow_up_notes: e.target.value, follow_up_required: !!e.target.value })} /></FormField>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEndOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={end} className="bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="tc-end-confirm">End & Save Encounter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InviteParticipantDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        consultationId={id}
        existingEmails={(c.video_invitees || []).map((i) => (i.email || "").toLowerCase())}
        onInvited={() => { setInviteOpen(false); load(); }}
      />
    </div>
  );
}

/* ============================================================================
 *  Invite Participant Dialog — used during an active call
 * ==========================================================================*/
function InviteParticipantDialog({ open, onOpenChange, consultationId, existingEmails, onInvited }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/users", { params: { page_size: 100 } })
      .then((r) => setUsers((r.data.items || []).filter((u) => u.email && u.status === "active")))
      .catch(() => {});
  }, [open]);

  const filtered = useMemo(() => {
    if (!users.length) return [];
    const q = search.toLowerCase();
    return users
      .filter((u) => {
        const matchesQ = !q || `${u.first_name} ${u.last_name} ${u.email} ${u.role_code}`.toLowerCase().includes(q);
        return matchesQ && !existingEmails.includes((u.email || "").toLowerCase());
      })
      .slice(0, 50);
  }, [users, search, existingEmails]);

  async function invite() {
    if (!picked) { toast.error("Select a user"); return; }
    setSaving(true);
    try {
      await api.post(`/teleconsult/consultations/${consultationId}/invite`, { user_id: picked });
      toast.success("Participant invited — SMS dispatched");
      onInvited();
      setPicked(null);
      setSearch("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="tc-invite-dialog">
        <DialogHeader><DialogTitle>Invite Participant</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <FormField label="Search H@H users (clinicians, coordinators, etc.)">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, email, or role…"
              data-testid="tc-invite-search"
            />
          </FormField>
          <div className="border border-[#E5E7EB] rounded max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-6 text-center text-xs text-[#6B7280]">
                {existingEmails.length > 0 ? "No matching users (already-invited people are filtered out)." : "No matches."}
              </div>
            ) : filtered.map((u) => (
              <div
                key={u.id}
                onClick={() => setPicked(u.id)}
                className={`flex items-center gap-2 px-2.5 py-1.5 cursor-pointer text-sm border-b border-[#E5E7EB] last:border-b-0 ${picked === u.id ? "bg-[#0B7E8C]/10" : "hover:bg-[#FAFBFC]"}`}
                data-testid={`tc-invite-option-${u.id}`}
              >
                <div className="w-5 h-5 rounded-full border flex items-center justify-center" style={{ borderColor: picked === u.id ? "#0B7E8C" : "#CBD5E1" }}>
                  {picked === u.id && <div className="w-2.5 h-2.5 rounded-full bg-[#0B7E8C]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[#1F2937] font-semibold truncate">{u.first_name} {u.last_name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{(u.role_code || "").replace("_", " ")} · {u.email}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-[#6B7280] italic">
            Inviting refreshes Watheeq room links — every participant gets an updated SMS with their new link/OTP.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5E7EB]">Cancel</Button>
          <Button onClick={invite} disabled={saving || !picked} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="tc-invite-confirm">
            {saving ? "Inviting…" : "Invite & Send Link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
